import { useEffect, useRef } from "react"

/**
 * Juggling cascade loader – three balls tracing a figure-8
 * with realistic scoop asymmetry (outer descent, inner ascent).
 *
 * Uses Tailwind spark color vars: --spark-orange, --spark-purple, --spark-yellow.
 *
 * @param size   – width in px (height = size × 0.8). Default 80.
 * @param period – cycle duration in ms. Default 1800.
 */

const CX = 20
const CY = 20
const AX = 13
const AY = 15
const TILT = 0.35
const SCOOP = 6
const TWO_PI = 2 * Math.PI
const P0 = 0
const P1 = TWO_PI / 3
const P2 = (2 * TWO_PI) / 3
const FRAME_BUDGET = 1000 / 45 // cap at 45fps

const pos = (angle: number, phase: number) => {
  const a = angle + phase

  return {
    x: CX - AX * Math.cos(a) + SCOOP * Math.sin(2 * a + TILT) * Math.cos(a),
    y: CY + AY * Math.cos(2 * a + TILT),
  }
}

type Props = {
  size?: number
  period?: number
  className?: string
}

export default function Juggling({
  size = 80,
  period = 1800,
  className,
}: Props) {
  const ball0Ref = useRef<SVGCircleElement>(null)
  const ball1Ref = useRef<SVGCircleElement>(null)
  const ball2Ref = useRef<SVGCircleElement>(null)

  useEffect(() => {
    let raf: number
    let last = 0

    function frame(t: number) {
      raf = requestAnimationFrame(frame)

      if (t - last < FRAME_BUDGET) {
        return
      }
      last = t

      const angle = (TWO_PI * (t % period)) / period

      const a = pos(angle, P0)
      const b = pos(angle, P1)
      const c = pos(angle, P2)

      ball0Ref.current?.setAttribute("cx", a.x.toFixed(2))
      ball0Ref.current?.setAttribute("cy", a.y.toFixed(2))
      ball1Ref.current?.setAttribute("cx", b.x.toFixed(2))
      ball1Ref.current?.setAttribute("cy", b.y.toFixed(2))
      ball2Ref.current?.setAttribute("cx", c.x.toFixed(2))
      ball2Ref.current?.setAttribute("cy", c.y.toFixed(2))
    }

    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
    }
  }, [period])

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      role="img"
      aria-label="Loading"
      className={className}
    >
      <circle ref={ball0Ref} r="4" className="fill-spark-orange" />
      <circle ref={ball1Ref} r="4" className="fill-spark-purple" />
      <circle ref={ball2Ref} r="4" className="fill-spark-yellow" />
    </svg>
  )
}
